import React from 'react'
import { useNavigate } from 'react-router-dom'
import { BusinessRecommendation } from '../../hooks/use_own_recommendation'
import BaseCard from './base_card'
import { EvaluationStarIcon } from '../common/star_icons'
import LoadingSpinner from '../loading/loading_spinner'
import FlatButton from '../buttons/flat_button'
import EvaluationButton from '../buttons/evaluation_button'
import { useBusinessIdeaUserEvaluation } from '../../hooks/use_business_idea_user_evaluation'
import { BusinessIdeaUserEvaluationType } from '../../enums/BusinessIdeaUserEvaluation'

type BusinessIdeaCardProps = {
  recommendation: BusinessRecommendation
  initialized: boolean
  isFullText?: boolean
  onDelete?: (businessIdeaId: string, projectName: string) => void
}

const BusinessIdeaCard: React.FC<BusinessIdeaCardProps> = ({
  recommendation,
  initialized,
  isFullText = false,
  onDelete,
}) => {
  const navigate = useNavigate()
  const { currentEvaluation, isEvaluating, evaluate } =
    useBusinessIdeaUserEvaluation(
      recommendation.businessIdeaId,
      recommendation.userEvaluation as BusinessIdeaUserEvaluationType | null
    )

  const handleClick = () => {
    navigate(`/business/detail/${recommendation.businessIdeaId}`)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation() // 親のonClickイベントを防ぐ
    if (onDelete) {
      onDelete(recommendation.businessIdeaId, recommendation.projectName)
    }
  }

  return (
    <BaseCard
      onClick={handleClick}
      className="cursor-pointer hover:shadow-lg transition-shadow"
    >
      <>
        {initialized ? (
          <>
            <div className="flex flex-col h-full">
              {/* 上部：おすすめラベルと星評価 */}
              <div className="bg-main-green text-main-white rounded-t px-4 py-2 flex flex-col items-center">
                <div className="flex items-center mb-1">
                  {/* 星評価 */}
                  <EvaluationStarIcon
                    evaluation={recommendation.evaluation}
                    size="md"
                    color="white"
                  />
                </div>
                <span className="text-xs">おすすめ事業アイディア</span>
              </div>

              {/* 中部：事業内容 */}
              <div className="p-4 flex-1">
                <h3
                  className={`font-bold text-lg mb-2 ${isFullText ? '' : 'line-clamp-3'}`}
                >
                  {recommendation.projectName}
                </h3>
                <p
                  className={`text-sm mb-3 ${isFullText ? '' : 'line-clamp-4'}`}
                >
                  {recommendation.businessContentFirst}
                </p>
                <h4 className="font-bold">マッチングPoint</h4>
                <p>{recommendation.keyPoint}</p>
              </div>

              {/* 企業情報 */}
              <div className="mt-auto">
                <div className="flex justify-between items-center mb-4">
                  {/* 評価ボタン */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <EvaluationButton
                      currentEvaluation={currentEvaluation}
                      onEvaluate={evaluate}
                      disabled={isEvaluating}
                    />
                  </div>
                  {/* 削除ボタン */}
                  {onDelete && (
                    <FlatButton
                      onClick={handleDelete}
                      className="bg-main-red text-white"
                    >
                      削除
                    </FlatButton>
                  )}
                </div>

                {/* 黒バー */}
                <div className="border-b border-main-black my-4"></div>

                <div className="flex items-center gap-2">
                  <img
                    src={recommendation.recommendationEnterprise.logoUrl}
                    alt="企業ロゴ"
                    className="w-12 h-12 rounded object-cover"
                  />
                  <div>
                    <p
                      className={`text-base font-bold ${isFullText ? '' : 'line-clamp-1'}`}
                    >
                      {recommendation.recommendationEnterprise.name}
                    </p>
                    {/* {recommendation.recommendationEnterprise.nameEn && (
                      <p
                        className={`text-xs text-main-darkgray ${isFullText ? '' : 'line-clamp-1'}`}
                      >
                        {recommendation.recommendationEnterprise.nameEn}
                      </p>
                    )} */}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <LoadingSpinner className="my-20" />
        )}
      </>
    </BaseCard>
  )
}

export default BusinessIdeaCard
